// Ticket Scrubbing — admin/test page for reviewing the rule-based classifier's
// output sprint-by-sprint. Lets a reviewer flag misclassifications so we can
// improve the rubric (or feed the data back to a future LLM pass).

import { useCallback, useMemo, useState } from 'react'
import { PULSE_COLORS } from './pulseConfig.js'

const CATEGORIES = [
    'feature', 'bug-fix', 'refactor', 'perf', 'infra', 'test',
    'spike', 'docs', 'research', 'support', 'chore',
]

const CATEGORY_COLOR = {
    feature: '#3b82f6',
    'bug-fix': '#ef4444',
    refactor: '#a78bfa',
    perf: '#f59e0b',
    infra: '#06b6d4',
    test: '#84cc16',
    spike: '#fbbf24',
    docs: '#94a3b8',
    research: '#c084fc',
    support: '#fb923c',
    chore: '#64748b',
}

const SOURCE_LABEL = {
    rules: { label: 'rules', color: PULSE_COLORS.success },
    fallback: { label: 'fallback', color: PULSE_COLORS.warning },
}

export default function TicketScrubbing() {
    const [sprintNum, setSprintNum] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null) // server response
    const [feedbackMap, setFeedbackMap] = useState({}) // issue_key → { suggested, note, saved }
    const [persist, setPersist] = useState(false) // dry-run vs persist
    const [filterCategory, setFilterCategory] = useState('all')
    const [filterSource, setFilterSource] = useState('all')
    const [filterText, setFilterText] = useState('')
    const [editing, setEditing] = useState(null) // issue_key currently being edited

    const runClassifier = useCallback(async () => {
        const num = Number(sprintNum)
        if (!Number.isFinite(num) || num < 1) {
            setError('Enter a sprint number (≥ 1)')
            return
        }
        setLoading(true)
        setError(null)
        setData(null)
        try {
            const url = `/api/pulse/classify/sprint?sprintNum=${num}${persist ? '' : '&dryRun=1'}`
            const r = await fetch(url, { method: 'POST' })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `classify ${r.status}`)
            setData(j)
            // Seed feedbackMap with anything already saved on the server.
            const seeded = {}
            for (const f of j.feedback || []) {
                seeded[f.issue_key] = {
                    suggested: f.suggested_category,
                    note: f.note || '',
                    submittedBy: f.submitted_by,
                    saved: true,
                }
            }
            setFeedbackMap(seeded)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [sprintNum, persist])

    const submitFeedback = useCallback(async (key, ticket, suggested, note) => {
        try {
            const r = await fetch('/api/pulse/classify/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    issueKey: key,
                    suggestedCategory: suggested,
                    note,
                    wasClassifiedAs: ticket.category,
                }),
            })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `feedback ${r.status}`)
            setFeedbackMap((prev) => ({
                ...prev,
                [key]: { suggested, note, saved: true, submittedBy: j.submittedBy },
            }))
            setEditing(null)
        } catch (e) {
            alert(`Failed to save feedback: ${e.message}`)
        }
    }, [])

    const filteredTickets = useMemo(() => {
        const list = data?.tickets || []
        const lower = filterText.trim().toLowerCase()
        return list.filter((t) => {
            if (filterCategory !== 'all' && t.category !== filterCategory) return false
            if (filterSource !== 'all' && t.source !== filterSource) return false
            if (lower) {
                const hay = `${t.key} ${t.summary} ${(t.labels || []).join(' ')} ${(t.components || []).join(' ')}`.toLowerCase()
                if (!hay.includes(lower)) return false
            }
            return true
        })
    }, [data, filterCategory, filterSource, filterText])

    const feedbackCount = Object.keys(feedbackMap).length

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <div>
                <h2 style={{ color: PULSE_COLORS.text, margin: 0, fontSize: '20px' }}>🧪 Ticket Scrubbing</h2>
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', maxWidth: '780px', marginTop: '4px' }}>
                    Test the rule-based classifier on a single sprint. Each ticket gets one of 11 categories
                    (feature, bug-fix, refactor, perf, infra, test, spike, docs, research, support, chore).
                    Click <strong style={{ color: PULSE_COLORS.text }}>Suggest different</strong> on any ticket
                    where the category looks wrong — feedback is saved per-reviewer and shows up here next time.
                </p>
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', padding: '12px', background: PULSE_COLORS.cardBg, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '8px' }}>
                <label style={{ color: PULSE_COLORS.text, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Sprint #
                    <input
                        type="number"
                        value={sprintNum}
                        onChange={(e) => setSprintNum(e.target.value)}
                        placeholder="e.g. 14"
                        style={{ width: '80px', background: PULSE_COLORS.bgInner, color: PULSE_COLORS.text, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px', padding: '6px 8px', fontSize: '13px' }}
                    />
                </label>
                <label style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                        type="checkbox"
                        checked={persist}
                        onChange={(e) => setPersist(e.target.checked)}
                    />
                    Persist classifications (uncheck for dry-run)
                </label>
                <button
                    onClick={runClassifier}
                    disabled={loading}
                    style={{
                        background: PULSE_COLORS.primary, color: PULSE_COLORS.text, border: 'none',
                        borderRadius: '4px', padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                        cursor: loading ? 'wait' : 'pointer',
                    }}
                >
                    {loading ? 'Classifying…' : '▶ Run classifier'}
                </button>
                {error && <span style={{ color: PULSE_COLORS.danger, fontSize: '12px' }}>{error}</span>}
                {data && (
                    <span style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', marginLeft: 'auto' }}>
                        v{data.classifier_version} · {data.total} tickets · {feedbackCount} reviewed
                    </span>
                )}
            </div>

            {/* Summary chips */}
            {data && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <CategoryChip
                        label="all"
                        count={data.total}
                        selected={filterCategory === 'all'}
                        onClick={() => setFilterCategory('all')}
                    />
                    {CATEGORIES.filter((c) => data.by_category[c]).map((c) => (
                        <CategoryChip
                            key={c}
                            label={c}
                            count={data.by_category[c]}
                            color={CATEGORY_COLOR[c]}
                            selected={filterCategory === c}
                            onClick={() => setFilterCategory(c)}
                        />
                    ))}
                </div>
            )}

            {/* Filter row */}
            {data && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: PULSE_COLORS.textMuted }}>
                    <span>Source:</span>
                    {['all', 'rules', 'fallback'].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilterSource(s)}
                            style={{
                                background: filterSource === s ? `${PULSE_COLORS.primary}30` : 'transparent',
                                color: filterSource === s ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                                border: `1px solid ${filterSource === s ? PULSE_COLORS.primary : PULSE_COLORS.border}`,
                                borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer',
                            }}
                        >
                            {s}{s !== 'all' ? ` (${data.by_source[s] || 0})` : ''}
                        </button>
                    ))}
                    <input
                        type="text"
                        placeholder="search title / key / label..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        style={{ flex: 1, minWidth: '200px', background: PULSE_COLORS.bgInner, color: PULSE_COLORS.text, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px', padding: '4px 8px', fontSize: '12px' }}
                    />
                    <span>{filteredTickets.length} shown</span>
                </div>
            )}

            {/* Ticket list */}
            {data && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {filteredTickets.map((t) => (
                        <TicketRow
                            key={t.key}
                            ticket={t}
                            feedback={feedbackMap[t.key]}
                            editing={editing === t.key}
                            onStartEdit={() => setEditing(t.key)}
                            onCancelEdit={() => setEditing(null)}
                            onSubmit={(suggested, note) => submitFeedback(t.key, t, suggested, note)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function CategoryChip({ label, count, color, selected, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                background: selected ? (color ? `${color}30` : `${PULSE_COLORS.primary}30`) : PULSE_COLORS.cardBg,
                color: selected ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                border: `1px solid ${selected && color ? color : PULSE_COLORS.border}`,
                borderRadius: '999px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
            }}
        >
            {color && <span style={{ width: '8px', height: '8px', background: color, borderRadius: '50%' }} />}
            <span>{label}</span>
            <strong>{count}</strong>
        </button>
    )
}

function TicketRow({ ticket, feedback, editing, onStartEdit, onCancelEdit, onSubmit }) {
    const [suggested, setSuggested] = useState(feedback?.suggested || ticket.category)
    const [note, setNote] = useState(feedback?.note || '')
    const wasFlagged = feedback?.saved
    const catColor = CATEGORY_COLOR[ticket.category] || PULSE_COLORS.textMuted
    const sourceMeta = SOURCE_LABEL[ticket.source] || { label: ticket.source, color: PULSE_COLORS.textMuted }
    const jiraUrl = `https://bstock.atlassian.net/browse/${ticket.key}`

    return (
        <div
            style={{
                padding: '10px 12px',
                background: PULSE_COLORS.cardBg,
                border: `1px solid ${wasFlagged ? PULSE_COLORS.warning : PULSE_COLORS.border}`,
                borderLeft: `4px solid ${wasFlagged ? PULSE_COLORS.warning : catColor}`,
                borderRadius: '6px',
                fontSize: '13px',
            }}
        >
            <div style={{ display: 'grid', gridTemplateColumns: '110px 70px 60px 1fr 140px 180px 110px', gap: '10px', alignItems: 'center' }}>
                <a href={jiraUrl} target="_blank" rel="noreferrer" style={{ color: PULSE_COLORS.primary, fontFamily: 'monospace', fontSize: '12px', textDecoration: 'none' }}>
                    {ticket.key}
                </a>
                <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                    {ticket.issuetype}
                </span>
                <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', fontFamily: 'monospace' }}>
                    {ticket.points}pt
                </span>
                <span style={{ color: PULSE_COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.summary}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '8px', height: '8px', background: catColor, borderRadius: '50%' }} />
                    <strong style={{ color: PULSE_COLORS.text, fontSize: '12px' }}>{ticket.category}</strong>
                    <span style={{ color: sourceMeta.color, fontSize: '10px', textTransform: 'uppercase' }}>{sourceMeta.label}</span>
                </span>
                <span title={ticket.rationale} style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.rationale}
                </span>
                <button
                    onClick={editing ? onCancelEdit : onStartEdit}
                    style={{
                        background: wasFlagged ? PULSE_COLORS.warning : 'transparent',
                        color: wasFlagged ? PULSE_COLORS.cardBg : PULSE_COLORS.textMuted,
                        border: `1px solid ${wasFlagged ? PULSE_COLORS.warning : PULSE_COLORS.border}`,
                        borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer',
                    }}
                >
                    {wasFlagged ? `→ ${feedback.suggested}` : editing ? 'Cancel' : '✎ Suggest different'}
                </button>
            </div>

            {editing && (
                <div style={{ marginTop: '10px', padding: '10px', background: PULSE_COLORS.bgInner, borderRadius: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>Should be:</span>
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setSuggested(c)}
                                style={{
                                    background: suggested === c ? `${CATEGORY_COLOR[c]}40` : 'transparent',
                                    color: suggested === c ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                                    border: `1px solid ${suggested === c ? CATEGORY_COLOR[c] : PULSE_COLORS.border}`,
                                    borderRadius: '999px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer',
                                }}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional: why does this category fit better? Cite labels/components/title patterns you'd like the classifier to catch."
                        rows={2}
                        style={{ background: PULSE_COLORS.cardBg, color: PULSE_COLORS.text, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px', padding: '6px 8px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical' }}
                    />
                    {(ticket.labels?.length || ticket.components?.length) ? (
                        <div style={{ fontSize: '11px', color: PULSE_COLORS.textSubtle }}>
                            {ticket.labels?.length > 0 && <span>Labels: {ticket.labels.join(', ')} </span>}
                            {ticket.components?.length > 0 && <span>· Components: {ticket.components.join(', ')}</span>}
                        </div>
                    ) : null}
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                            onClick={onCancelEdit}
                            style={{ background: 'transparent', color: PULSE_COLORS.textMuted, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => onSubmit(suggested, note)}
                            disabled={suggested === ticket.category && !note.trim()}
                            style={{
                                background: PULSE_COLORS.primary, color: PULSE_COLORS.text, border: 'none',
                                borderRadius: '4px', padding: '4px 10px', fontSize: '12px',
                                cursor: suggested === ticket.category && !note.trim() ? 'not-allowed' : 'pointer',
                                opacity: suggested === ticket.category && !note.trim() ? 0.5 : 1,
                            }}
                        >
                            Submit
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
